import React, { useEffect, useState, useCallback } from 'react';
import { usersAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Users, Edit, Trash2, Save, Search, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editUser, setEditUser] = useState(null);
  const [pwDialog, setPwDialog] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const allRoles = ['Admin', 'Editor', 'Revisor', 'Autor'];

  const loadUsers = useCallback(async () => {
    try {
      const res = await usersAPI.list();
      setUsers(res.data.items || res.data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleUpdateUser = async () => {
    if (!editUser) return;
    try {
      await usersAPI.update(editUser.id, { roles: editUser.roles, name: editUser.name });
      setEditUser(null);
      toast.success('Usuarie actualizado');
      loadUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al actualizar'); }
  };

  const handleChangePassword = async () => {
    if (!pwDialog || !newPassword) return;
    if (newPassword.length < 6) { toast.error('La contrasena debe tener al menos 6 caracteres'); return; }
    try {
      await usersAPI.changePassword(pwDialog.id, newPassword);
      setPwDialog(null);
      setNewPassword('');
      toast.success('Contrasena cambiada exitosamente');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al cambiar contrasena'); }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm('Eliminar este usuarie?')) return;
    try { await usersAPI.delete(id); toast.success('Usuarie eliminade'); loadUsers(); } catch { toast.error('Error al eliminar'); }
  };

  const filtered = users.filter(u => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q) || (u.apellidos || '').toLowerCase().includes(q);
  });

  if (loading) return <div className="flex-1 flex justify-center items-center"><div className="animate-spin w-6 h-6 border-2 border-trans-blue border-t-transparent rounded-full" /></div>;

  return (
    <div className="flex-1 p-4 sm:p-6 lg:p-8 overflow-auto" data-testid="users-page">
      <div className="max-w-6xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-serif font-bold text-slate-dark">Gestion de Usuaries</h1>
            <p className="text-sm font-sans text-gray-400 mt-1">{users.length} usuaries registrades</p>
          </div>
        </div>

        <div className="relative max-w-sm mb-6">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" strokeWidth={1.5} />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por nombre o email..." className="pl-9 font-sans text-sm" data-testid="search-users" />
        </div>

        <div className="bg-white border border-gray-100 rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-sans">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Nombre</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Roles</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Suscripcion</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Cuenta</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((u, i) => (
                  <tr key={u.id} className="hover:bg-gray-50/50" data-testid={`user-row-${i}`}>
                    <td className="px-4 py-3 font-medium text-slate-dark">{u.name} {u.apellidos}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{u.email}</td>
                    <td className="px-4 py-3"><div className="flex gap-1 flex-wrap">{u.roles?.map(r => <Badge key={r} variant="outline" className="text-[10px]">{r}</Badge>)}</div></td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className={`text-[10px] ${u.subscription_status === 'active' ? 'bg-green-50 text-green-700' : u.subscription_status === 'trial' ? 'bg-blue-50 text-blue-700' : 'bg-red-50 text-red-700'}`}>
                        {u.subscription_status || 'N/A'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-sans ${u.claimed ? 'text-green-600' : 'text-gray-400'}`}>
                        {u.claimed ? 'Reclamada' : 'Pendiente'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setEditUser({ ...u })} data-testid={`edit-user-${i}`} title="Editar"><Edit className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-amber-500" onClick={() => { setPwDialog(u); setNewPassword(''); }} data-testid={`change-pw-${i}`} title="Cambiar contrasena"><KeyRound className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400" onClick={() => handleDeleteUser(u.id)} data-testid={`delete-user-${i}`} title="Eliminar"><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Dialog open={!!editUser} onOpenChange={() => setEditUser(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-serif">Editar Usuarie</DialogTitle></DialogHeader>
          {editUser && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-sans font-medium">Nombre</label>
                <Input value={editUser.name || ''} onChange={e => setEditUser(u => ({ ...u, name: e.target.value }))} className="mt-1 font-sans" />
              </div>
              <div>
                <label className="text-sm font-sans font-medium">Roles</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {allRoles.map(r => (
                    <label key={r} className="flex items-center gap-1.5 text-sm font-sans">
                      <input type="checkbox" checked={editUser.roles?.includes(r)} onChange={e => {
                        setEditUser(u => ({
                          ...u,
                          roles: e.target.checked ? [...(u.roles || []), r] : (u.roles || []).filter(x => x !== r)
                        }));
                      }} className="rounded" />
                      {r}
                    </label>
                  ))}
                </div>
              </div>
              <Button onClick={handleUpdateUser} className="w-full bg-slate-dark font-sans" data-testid="save-user-btn"><Save className="w-4 h-4 mr-1" /> Guardar</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwDialog} onOpenChange={() => setPwDialog(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle className="font-serif">Cambiar Contrasena</DialogTitle></DialogHeader>
          {pwDialog && (
            <div className="space-y-4">
              <p className="text-sm font-sans text-gray-500">Cambiar contrasena de <strong>{pwDialog.name} {pwDialog.apellidos}</strong> ({pwDialog.email})</p>
              <div>
                <label className="text-sm font-sans font-medium">Nueva contrasena</label>
                <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Minimo 6 caracteres" className="mt-1 font-sans" data-testid="new-password-input" />
              </div>
              <Button onClick={handleChangePassword} className="w-full bg-slate-dark font-sans" disabled={newPassword.length < 6} data-testid="confirm-change-pw-btn">
                <KeyRound className="w-4 h-4 mr-1" /> Cambiar Contrasena
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
